import * as React from 'react';

import moment from 'moment';

import Imgix from 'js/components/Imgix';
import redirect from 'js/lib/coursera.redirect';

import { Box, Col, Container, Row, StyleSheet, color, css, spacing } from '@coursera/coursera-ui';

import type { EnterpriseContract } from 'bundles/enterprise-admin-account/constants/types';
import {
  getContractTerminationTime,
  getExpirationReason,
  isFreeTrialContract,
} from 'bundles/enterprise-admin-account/utils/AdminDashboardAccountUtils';
import {
  createProgramIcon,
  learnersIcon,
  planIcon,
  s3CourseraAssetURL,
} from 'bundles/enterprise-admin-constants/assets';
import ExpiredFaq from 'bundles/enterprise-admin-home/components/ExpiredFaq';
import {
  getExpirationDescription,
  getExpirationHeader,
} from 'bundles/enterprise-admin-home/components/ExpiredFaqsUtils';
import { getPurchaseLinkWithOrg } from 'bundles/enterprise-legacy-learner-home/utils/programUrlUtils';
import { Button } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-home';

const styles = StyleSheet.create({
  background: {
    backgroundColor: color.white,
  },
  header: {
    textAlign: 'center',
    margin: `112px ${spacing.md} ${spacing.md} ${spacing.md}`,
  },
  subHeader: {
    marginBottom: '4rem',
    textAlign: 'center',
    fontWeight: '400',
    fontSize: 20,
    lineHeight: '24px',
  },
  logo: {
    objectFit: 'contain',
    margin: `3rem 0 ${spacing.lg}`,
  },
  container: {
    maxWidth: '1200px',
  },
  column: {
    textAlign: 'center',
  },
  icon: {
    objectFit: 'contain',
    margin: '0 auto',
  },
  iconLabel: {
    margin: `${spacing.lg} 0 ${spacing.xl}`,
    fontWeight: '600',
    textAlign: 'center',
  },
  description: {
    margin: `0 7rem ${spacing.lg}`,
    textAlign: 'center',
    maxWidth: '850px',
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '28px',
  },
  buttonContainer: {
    marginBottom: spacing.xxl,
  },
});

const LOCALE_STRING = 'LL';

export type Props = {
  thirdPartyOrganizationId: string;
  expiredContract: EnterpriseContract;
};

function AdminLandingExpired({ thirdPartyOrganizationId, expiredContract }: Props) {
  const expirationDate = moment(getContractTerminationTime(expiredContract)).format(LOCALE_STRING);
  const isFreeTrial = isFreeTrialContract(expiredContract);
  const expirationReason = getExpirationReason(expiredContract);
  return (
    <div {...css(styles.background)}>
      <Box justifyContent="center" flexDirection="column" alignItems="center">
        <h1 {...css(styles.header)}>{getExpirationHeader(expirationReason, isFreeTrial)}</h1>
        <h3 {...css(styles.subHeader)}>
          {_t('If you’d like your team to continue learning on Coursera, follow these steps:')}
        </h3>

        <Container rootClassName={styles.container}>
          <Row alignItems="end">
            <Col md={4} sm={12} rootClassName={styles.column}>
              <Imgix
                {...css(styles.icon)}
                src={s3CourseraAssetURL + createProgramIcon}
                alt={_t('Buy a new contract')}
                maxWidth={125}
                maxHeight={125}
              />
              <h3 {...css(styles.iconLabel)}>{_t('Buy a new contract')}</h3>
            </Col>
            <Col md={4} sm={12} rootClassName={styles.column}>
              <Imgix
                {...css(styles.icon)}
                src={s3CourseraAssetURL + planIcon}
                alt={_t('Create a new learning program')}
                maxWidth={125}
                maxHeight={125}
              />
              <h3 {...css(styles.iconLabel)}>{_t('Create a new learning program')}</h3>
            </Col>
            <Col md={4} sm={12} rootClassName={styles.column}>
              <Imgix
                {...css(styles.icon)}
                src={s3CourseraAssetURL + learnersIcon}
                alt={_t('Re-invite learners')}
                maxWidth={125}
                maxHeight={125}
              />
              <h3 {...css(styles.iconLabel)}>{_t('Re-invite learners')}</h3>
            </Col>
          </Row>
        </Container>
        <h3 {...css(styles.description)}>{getExpirationDescription(expirationReason, expirationDate, isFreeTrial)}</h3>
        <Box justifyContent="center" rootClassName={styles.buttonContainer}>
          <Button
            style={{
              paddingTop: '1.5rem !important',
              paddingBottom: '1.5rem !important',
              paddingLeft: '6rem !important',
              paddingRight: '6rem !important',
            }}
            variant="primary"
            onClick={() => redirect.setLocation(getPurchaseLinkWithOrg({ thirdPartyOrganizationId }))}
          >
            {_t('Buy new contract')}
          </Button>
        </Box>
      </Box>
      <ExpiredFaq />
    </div>
  );
}

export default AdminLandingExpired;
